/** Client-side device fingerprint hash.
 *
 *  Combines several browser signals (canvas, webgl, audio, screen, timezone,
 *  platform) into a SHA-256 hash. This is **not** a person identifier — it is
 *  a device cluster signal, used in the `X-Device-Fingerprint` header sent to
 *  /auth/select-org so the backend can detect anomalous device changes per
 *  session.
 *
 *  Each signal is captured in a try/catch — if a browser blocks/lacks any of
 *  them (privacy mode, headless, exotic UA), we just skip that signal and
 *  hash whatever survived. Worst case we still have screen + timezone +
 *  user-agent, which yields a stable 64-char hex string. The backend treats
 *  the hash as opaque, so degraded fingerprints simply have lower entropy. */

const STORAGE_KEY = 'dominoris.deviceFingerprint';

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function safeRun(label: string, fn: () => string): string {
  try {
    return `${label}:${fn()}`;
  } catch {
    return `${label}:err`;
  }
}

function canvasSignal(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 50;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no-2d-context');
  ctx.textBaseline = 'top';
  ctx.font = '14px "Arial"';
  ctx.fillStyle = '#f60';
  ctx.fillRect(0, 0, 100, 30);
  ctx.fillStyle = '#069';
  ctx.fillText('dominoris-fp 🛡', 2, 2);
  ctx.strokeStyle = '#0f0';
  ctx.beginPath();
  ctx.arc(40, 25, 10, 0, Math.PI * 2);
  ctx.stroke();
  return canvas.toDataURL();
}

function webglSignal(): string {
  const canvas = document.createElement('canvas');
  const gl =
    (canvas.getContext('webgl') as WebGLRenderingContext | null) ??
    (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
  if (!gl) throw new Error('no-webgl');
  const vendor = gl.getParameter(gl.VENDOR);
  const renderer = gl.getParameter(gl.RENDERER);
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const unmaskedVendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : '';
  const unmaskedRenderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : '';
  return `${String(vendor)}|${String(renderer)}|${String(unmaskedVendor)}|${String(unmaskedRenderer)}`;
}

interface OfflineAudioContextCtor {
  new (channels: number, length: number, sampleRate: number): OfflineAudioContext;
}

function audioSignal(): string {
  const Ctor =
    (window as unknown as { OfflineAudioContext?: OfflineAudioContextCtor })
      .OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: OfflineAudioContextCtor })
      .webkitOfflineAudioContext;
  if (!Ctor) throw new Error('no-offline-audio');
  const ctx = new Ctor(1, 5000, 44100);
  const oscillator = ctx.createOscillator();
  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(10000, ctx.currentTime);
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.setValueAtTime(-50, ctx.currentTime);
  compressor.knee.setValueAtTime(40, ctx.currentTime);
  compressor.ratio.setValueAtTime(12, ctx.currentTime);
  compressor.attack.setValueAtTime(0, ctx.currentTime);
  compressor.release.setValueAtTime(0.25, ctx.currentTime);
  oscillator.connect(compressor);
  compressor.connect(ctx.destination);
  oscillator.start(0);
  // Synchronous proxy: hash the parameters, not the rendered buffer.
  // Rendering is async and we don't want to block fingerprint generation.
  return `${ctx.sampleRate}|${ctx.length}|${compressor.threshold.value}|${compressor.knee.value}`;
}

function screenSignal(): string {
  const s = window.screen;
  return `${s.width}x${s.height}x${s.colorDepth}|${window.devicePixelRatio}`;
}

function timezoneSignal(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  return `${tz}|${new Date().getTimezoneOffset()}`;
}

function platformSignal(): string {
  const nav = window.navigator;
  const langs = Array.isArray(nav.languages) ? nav.languages.join(',') : nav.language ?? '';
  return `${nav.platform}|${nav.hardwareConcurrency ?? 0}|${langs}|${nav.userAgent}`;
}

/** Generates a stable SHA-256 hash (64 chars hex) representing this device.
 *  Uses canvas + webgl + audio + screen + timezone + platform.
 *  Privacy: NOT a person identifier — just a device cluster signal.
 *  Use for header X-Device-Fingerprint sent to /auth/select-org. */
export async function getDeviceFingerprint(): Promise<string> {
  if (typeof window === 'undefined') return '';

  try {
    const cached = window.sessionStorage.getItem(STORAGE_KEY);
    if (cached && /^[0-9a-f]{64}$/.test(cached)) {
      return cached;
    }
  } catch {
    // sessionStorage may throw in private mode — proceed without cache.
  }

  const parts = [
    safeRun('canvas', canvasSignal),
    safeRun('webgl', webglSignal),
    safeRun('audio', audioSignal),
    safeRun('screen', screenSignal),
    safeRun('tz', timezoneSignal),
    safeRun('platform', platformSignal)
  ];

  const hash = await sha256Hex(parts.join('||'));

  try {
    window.sessionStorage.setItem(STORAGE_KEY, hash);
  } catch {
    // ignore — non-fatal
  }

  return hash;
}
